/**
 * SchoolDining — per-tenant dining/meals management.
 * Tabs: Overview · Meal Plans · Menu · Subscriptions · Today
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UtensilsCrossed, Plus, Trash2, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const h = () => authHeaders();
const api = {
  overview: (t) => axios.get(`${API_URL}/api/school/tenants/${t}/dining/overview`, { headers: h() }).then(r => r.data),
  plans: (t) => axios.get(`${API_URL}/api/school/tenants/${t}/dining/plans`, { headers: h() }).then(r => r.data),
  createPlan: (t, d) => axios.post(`${API_URL}/api/school/tenants/${t}/dining/plans`, d, { headers: h() }).then(r => r.data),
  menu: (t) => axios.get(`${API_URL}/api/school/tenants/${t}/dining/menu`, { headers: h() }).then(r => r.data),
  createMenu: (t, d) => axios.post(`${API_URL}/api/school/tenants/${t}/dining/menu`, d, { headers: h() }).then(r => r.data),
  deleteMenu: (t, id) => axios.delete(`${API_URL}/api/school/tenants/${t}/dining/menu/${id}`, { headers: h() }).then(r => r.data),
  subs: (t) => axios.get(`${API_URL}/api/school/tenants/${t}/dining/subscriptions`, { headers: h() }).then(r => r.data),
  subscribe: (t, d) => axios.post(`${API_URL}/api/school/tenants/${t}/dining/subscriptions`, d, { headers: h() }).then(r => r.data),
  students: (t) => axios.get(`${API_URL}/api/school/tenants/${t}/students`, { headers: h() }).then(r => r.data),
  todayLogs: (t) => axios.get(`${API_URL}/api/school/tenants/${t}/dining/meal-logs/today`, { headers: h() }).then(r => r.data),
  logMeal: (t, d) => axios.post(`${API_URL}/api/school/tenants/${t}/dining/meal-logs`, d, { headers: h() }).then(r => r.data),
};
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const MEALS = ['breakfast', 'lunch', 'dinner', 'snack'];

export const SchoolDining = ({ tenantId }) => {
  const qc = useQueryClient();
  const [tab, setTab] = useState('overview');
  const { data: overview } = useQuery({ queryKey: ['dining-overview', tenantId], queryFn: () => api.overview(tenantId) });
  const { data: plans = [] } = useQuery({ queryKey: ['dining-plans', tenantId], queryFn: () => api.plans(tenantId) });
  const { data: menu = [] } = useQuery({ queryKey: ['dining-menu', tenantId], queryFn: () => api.menu(tenantId) });
  const { data: subs = [] } = useQuery({ queryKey: ['dining-subs', tenantId], queryFn: () => api.subs(tenantId) });
  const { data: students = [] } = useQuery({ queryKey: ['dining-students', tenantId], queryFn: () => api.students(tenantId) });
  const { data: today = [] } = useQuery({ queryKey: ['dining-today', tenantId], queryFn: () => api.todayLogs(tenantId) });
  const refresh = () => ['dining-overview', 'dining-plans', 'dining-menu', 'dining-subs', 'dining-today'].forEach(k => qc.invalidateQueries({ queryKey: [k, tenantId] }));

  const [planForm, setPlanForm] = useState({ name: '', description: '', price: 0, meal_types: [] });
  const [menuForm, setMenuForm] = useState({ day_of_week: 'monday', meal_type: 'lunch', name: '', description: '', ingredients: '' });
  const [subForm, setSubForm] = useState({ student_id: '', plan_id: '' });
  const studentList = Array.isArray(students) ? students : (students?.students || []);

  const submitPlan = async () => { try { await api.createPlan(tenantId, { ...planForm, price: Number(planForm.price) || 0 }); toast.success('Plan created'); setPlanForm({ name: '', description: '', price: 0, meal_types: [] }); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const submitMenu = async () => { try { await api.createMenu(tenantId, { ...menuForm, ingredients: menuForm.ingredients.split(',').map(s => s.trim()).filter(Boolean) }); toast.success('Menu added'); setMenuForm({ ...menuForm, name: '', description: '', ingredients: '' }); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const submitSub = async () => { try { await api.subscribe(tenantId, subForm); toast.success('Subscribed'); setSubForm({ student_id: '', plan_id: '' }); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };
  const logServe = async (student_id, meal_type) => { try { const r = await api.logMeal(tenantId, { student_id, meal_type }); if (r.allergy_conflicts?.length) toast.error('ALLERGY CONFLICT! Alert sent.'); else toast.success('Meal logged'); refresh(); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } };

  return (
    <div className="space-y-4" data-testid="school-dining">
      <div><h2 className="text-xl font-bold flex items-center gap-2"><UtensilsCrossed className="h-5 w-5 text-orange-500" /> Dining & Meals</h2><p className="text-sm text-slate-500">Meal plans, menus, and daily meal service.</p></div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="dining-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="plans" data-testid="dining-tab-plans">Plans</TabsTrigger>
          <TabsTrigger value="menu" data-testid="dining-tab-menu">Menu</TabsTrigger>
          <TabsTrigger value="subs" data-testid="dining-tab-subs">Subscriptions</TabsTrigger>
          <TabsTrigger value="today" data-testid="dining-tab-today">Today</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KPI label="Plans" v={overview?.total_plans ?? 0} />
            <KPI label="Subscribers" v={overview?.active_subscribers ?? 0} />
            <KPI label="Menu items" v={overview?.menu_items ?? 0} />
            <KPI label="Today's meals" v={overview?.meals_served_today ?? 0} accent="emerald" />
            <KPI label="Allergy flags" v={overview?.allergy_conflicts_today ?? 0} accent="rose" />
          </div>
        </TabsContent>

        <TabsContent value="plans" className="space-y-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> New Plan</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-4 gap-2">
              <Input placeholder="Plan name" value={planForm.name} onChange={e => setPlanForm({ ...planForm, name: e.target.value })} data-testid="dining-plan-name" />
              <Input type="number" placeholder="Price" value={planForm.price} onChange={e => setPlanForm({ ...planForm, price: e.target.value })} />
              <Input placeholder="Meals (comma)" value={planForm.meal_types.join(',')} onChange={e => setPlanForm({ ...planForm, meal_types: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
              <Button onClick={submitPlan} data-testid="dining-create-plan-btn">Create</Button>
            </CardContent>
          </Card>
          <div className="grid md:grid-cols-2 gap-3">
            {plans.map(p => (
              <Card key={p.id} data-testid={`dining-plan-${p.id}`}>
                <CardContent className="py-3">
                  <p className="text-sm font-semibold">{p.name} <Badge variant="outline" className="ml-1 text-[10px]">{p.subscriber_count} subscribers</Badge></p>
                  <p className="text-[11px] text-slate-500">{(p.meal_types || []).join(' · ') || 'No meals set'} · ${p.price}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="menu" className="space-y-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> Add Menu Item</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-6 gap-2">
              <Select value={menuForm.day_of_week} onValueChange={v => setMenuForm({ ...menuForm, day_of_week: v })}>
                <SelectTrigger data-testid="dining-menu-day"><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={menuForm.meal_type} onValueChange={v => setMenuForm({ ...menuForm, meal_type: v })}>
                <SelectTrigger data-testid="dining-menu-meal"><SelectValue /></SelectTrigger>
                <SelectContent>{MEALS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
              <Input className="md:col-span-2" placeholder="Dish name" value={menuForm.name} onChange={e => setMenuForm({ ...menuForm, name: e.target.value })} data-testid="dining-menu-name" />
              <Input placeholder="Ingredients (comma)" value={menuForm.ingredients} onChange={e => setMenuForm({ ...menuForm, ingredients: e.target.value })} />
              <Button onClick={submitMenu} data-testid="dining-add-menu-btn">Add</Button>
            </CardContent>
          </Card>
          <Card><CardContent className="py-2">
            {menu.length === 0 && <p className="text-sm text-slate-500 py-4 text-center">No menu items yet.</p>}
            {menu.map(m => (
              <div key={m.id} className="flex items-center gap-2 py-1.5 border-b last:border-0" data-testid={`dining-menu-${m.id}`}>
                <Badge variant="outline" className="text-[10px] capitalize">{m.day_of_week}</Badge>
                <Badge variant="outline" className="text-[10px] capitalize">{m.meal_type}</Badge>
                <span className="text-sm font-medium flex-1 truncate">{m.name}</span>
                <span className="text-[11px] text-slate-500 truncate max-w-xs">{(m.ingredients || []).join(', ')}</span>
                <Button size="sm" variant="ghost" onClick={() => api.deleteMenu(tenantId, m.id).then(refresh)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="subs" className="space-y-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Plus className="h-4 w-4" /> Subscribe Student</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-2">
              <Select value={subForm.student_id} onValueChange={v => setSubForm({ ...subForm, student_id: v })}>
                <SelectTrigger data-testid="dining-sub-student"><SelectValue placeholder="Student" /></SelectTrigger>
                <SelectContent className="max-h-64">{studentList.slice(0, 200).map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={subForm.plan_id} onValueChange={v => setSubForm({ ...subForm, plan_id: v })}>
                <SelectTrigger data-testid="dining-sub-plan"><SelectValue placeholder="Plan" /></SelectTrigger>
                <SelectContent>{plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={submitSub} data-testid="dining-subscribe-btn">Subscribe</Button>
            </CardContent>
          </Card>
          <Card><CardContent className="py-2">
            {subs.length === 0 && <p className="text-sm text-slate-500 py-4 text-center">No subscriptions yet.</p>}
            {subs.map(s => (
              <div key={s.id} className="flex items-center gap-3 py-1.5 border-b last:border-0" data-testid={`dining-sub-${s.id}`}>
                <span className="text-sm font-medium flex-1">{s.student_name} <span className="text-[11px] text-slate-500">({s.admission_number})</span></span>
                <Badge variant="outline" className="text-[10px]">{s.plan_name}</Badge>
                {s.allergies?.length > 0 && <Badge className="text-[10px] bg-rose-100 text-rose-700 border-rose-200"><AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> {s.allergies.join(', ')}</Badge>}
              </div>
            ))}
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="today" className="space-y-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Quick Serve</CardTitle></CardHeader>
            <CardContent>
              <p className="text-xs text-slate-500 mb-2">Pick a student + meal to log the service. Allergy conflicts fire instant alerts.</p>
              <div className="grid md:grid-cols-6 gap-2">
                {MEALS.map(m => (
                  <div key={m} className="text-center">
                    <p className="text-xs font-semibold capitalize mb-1">{m}</p>
                    <Select onValueChange={v => logServe(v, m)}>
                      <SelectTrigger data-testid={`dining-serve-${m}`}><SelectValue placeholder="Student…" /></SelectTrigger>
                      <SelectContent className="max-h-64">{studentList.slice(0, 150).map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card><CardHeader className="pb-2"><CardTitle className="text-sm">Today's Service Log</CardTitle></CardHeader><CardContent className="py-2">
            {today.length === 0 && <p className="text-sm text-slate-500 py-4 text-center">No meals logged today.</p>}
            {today.map(l => (
              <div key={l.id} className="flex items-center gap-2 py-1.5 border-b last:border-0">
                {l.allergy_conflicts?.length > 0 ? <AlertTriangle className="h-4 w-4 text-rose-500" /> : <CheckCircle className="h-4 w-4 text-emerald-500" />}
                <span className="text-sm flex-1">{l.student_name}</span>
                <Badge variant="outline" className="text-[10px] capitalize">{l.meal_type}</Badge>
                <span className="text-[10px] text-slate-500">{new Date(l.logged_at).toLocaleTimeString()}</span>
              </div>
            ))}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const KPI = ({ label, v, accent }) => <Card><CardContent className="py-4"><p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p><p className={`text-2xl font-bold mt-1 ${accent === 'emerald' ? 'text-emerald-600' : accent === 'rose' ? 'text-rose-600' : 'text-slate-900'}`}>{v}</p></CardContent></Card>;

export default SchoolDining;
